'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Alert {
  id: string;
  type: 'critical' | 'warning' | 'info' | 'success';
  category: 'shipment' | 'system' | 'customer' | 'team';
  title: string;
  message: string;
  timestamp: string;
  read: boolean;
  actionRequired: boolean;
}

const mockAlerts: Alert[] = [
  {
    id: '1',
    type: 'critical',
    category: 'shipment',
    title: 'Shipment Delay Alert',
    message: 'TRK-2026-001234 is delayed by 4 hours due to weather conditions in Chicago',
    timestamp: new Date(Date.now() - 5 * 60000).toISOString(),
    read: false,
    actionRequired: true,
  },
  {
    id: '2',
    type: 'warning',
    category: 'shipment',
    title: 'Customs Clearance Required',
    message: 'TRK-2026-005678 requires additional documentation for customs clearance',
    timestamp: new Date(Date.now() - 15 * 60000).toISOString(),
    read: false,
    actionRequired: true,
  },
  {
    id: '3',
    type: 'info',
    category: 'customer',
    title: 'New Customer Registration',
    message: 'Emily Brown has registered and placed their first order',
    timestamp: new Date(Date.now() - 30 * 60000).toISOString(),
    read: false,
    actionRequired: false,
  },
  {
    id: '4',
    type: 'success',
    category: 'shipment',
    title: 'Delivery Completed',
    message: 'TRK-2026-009012 successfully delivered to Sydney, Australia',
    timestamp: new Date(Date.now() - 45 * 60000).toISOString(),
    read: true,
    actionRequired: false,
  },
  {
    id: '5',
    type: 'warning',
    category: 'team',
    title: 'Driver Availability Low',
    message: 'Only 2 drivers available for next shift. Consider scheduling adjustments.',
    timestamp: new Date(Date.now() - 60 * 60000).toISOString(),
    read: false,
    actionRequired: true,
  },
  {
    id: '6',
    type: 'critical',
    category: 'system',
    title: 'System Performance Alert',
    message: 'Database response time increased by 40%. Monitoring required.',
    timestamp: new Date(Date.now() - 90 * 60000).toISOString(),
    read: false,
    actionRequired: true,
  },
];

export default function RealtimeAlerts() {
  const [alerts, setAlerts] = useState<Alert[]>(mockAlerts);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterCategory, setFilterCategory] = useState<string>('all');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);

  const filteredAlerts = alerts.filter((alert) => {
    const matchesType = filterType === 'all' || alert.type === filterType;
    const matchesCategory = filterCategory === 'all' || alert.category === filterCategory;
    const matchesRead = !showUnreadOnly || !alert.read;
    return matchesType && matchesCategory && matchesRead;
  });

  const unreadCount = alerts.filter((a) => !a.read).length;
  const criticalCount = alerts.filter((a) => a.type === 'critical' && !a.read).length;
  const actionRequiredCount = alerts.filter((a) => a.actionRequired && !a.read).length;

  const markAsRead = (alertId: string) => {
    setAlerts(alerts.map((a) => (a.id === alertId ? { ...a, read: true } : a)));
  };

  const markAllAsRead = () => {
    setAlerts(alerts.map((a) => ({ ...a, read: true })));
  };

  const getAlertIcon = (type: string) => {
    switch (type) {
      case 'critical':
        return 'ExclamationCircleIcon';
      case 'warning':
        return 'ExclamationTriangleIcon';
      case 'info':
        return 'InformationCircleIcon';
      case 'success':
        return 'CheckCircleIcon';
      default:
        return 'BellIcon';
    }
  };

  const getAlertColor = (type: string) => {
    switch (type) {
      case 'critical':
        return 'text-destructive bg-destructive/10 border-destructive/20';
      case 'warning':
        return 'text-accent bg-accent/10 border-accent/20';
      case 'info':
        return 'text-primary bg-primary/10 border-primary/20';
      case 'success':
        return 'text-success bg-success/10 border-success/20';
      default:
        return 'text-muted-foreground bg-muted border-border';
    }
  };

  const formatTimestamp = (timestamp: string) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minutes ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)} hours ago`;
    return `${Math.floor(diffMins / 1440)} days ago`;
  };

  return (
    <div className="space-y-6">
      {/* Alert Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="BellIcon" size={24} className="text-primary" />
            <h3 className="text-sm font-medium text-muted-foreground">Total Alerts</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{alerts.length}</p>
        </div>
        <div className="bg-card rounded-xl p-6 shadow-card border border-destructive/20">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="ExclamationCircleIcon" size={24} className="text-destructive" />
            <h3 className="text-sm font-medium text-muted-foreground">Critical</h3>
          </div>
          <p className="text-3xl font-bold text-destructive">{criticalCount}</p>
        </div>
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="EnvelopeIcon" size={24} className="text-accent" />
            <h3 className="text-sm font-medium text-muted-foreground">Unread</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{unreadCount}</p>
        </div>
        <div className="bg-card rounded-xl p-6 shadow-card border border-border">
          <div className="flex items-center gap-3 mb-2">
            <Icon name="ClipboardDocumentCheckIcon" size={24} className="text-secondary" />
            <h3 className="text-sm font-medium text-muted-foreground">Action Required</h3>
          </div>
          <p className="text-3xl font-bold text-foreground">{actionRequiredCount}</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-xl p-6 shadow-card border border-border">
        <div className="flex flex-wrap items-center gap-4">
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-foreground mb-2">Alert Type</label>
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              <option value="all">All Types</option>
              <option value="critical">Critical</option>
              <option value="warning">Warning</option>
              <option value="info">Info</option>
              <option value="success">Success</option>
            </select>
          </div>
          <div className="flex-1 min-w-[200px]">
            <label className="block text-sm font-medium text-foreground mb-2">Category</label>
            <select
              value={filterCategory}
              onChange={(e) => setFilterCategory(e.target.value)}
              className="w-full px-4 py-2 border border-input rounded-lg focus:outline-none focus:ring-2 focus:ring-ring text-foreground"
            >
              <option value="all">All Categories</option>
              <option value="shipment">Shipment</option>
              <option value="system">System</option>
              <option value="customer">Customer</option>
              <option value="team">Team</option>
            </select>
          </div>
          <div className="flex items-end gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showUnreadOnly}
                onChange={(e) => setShowUnreadOnly(e.target.checked)}
                className="w-4 h-4 text-primary border-input rounded focus:ring-2 focus:ring-ring"
              />
              <span className="text-sm text-foreground">Unread only</span>
            </label>
            <button
              onClick={markAllAsRead}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium"
            >
              Mark All Read
            </button>
          </div>
        </div>
      </div>

      {/* Alerts List */}
      <div className="space-y-4">
        {filteredAlerts.map((alert) => (
          <div
            key={alert.id}
            className={`bg-card rounded-xl p-6 shadow-card border-2 transition-all ${
              alert.read ? 'border-border opacity-70' : getAlertColor(alert.type)
            }`}
          >
            <div className="flex items-start gap-4">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ${getAlertColor(alert.type)}`}>
                <Icon name={getAlertIcon(alert.type)} size={24} />
              </div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">{alert.title}</h3>
                    <div className="flex items-center gap-3 text-sm text-muted-foreground">
                      <span className="capitalize">{alert.category}</span>
                      <span>•</span>
                      <span>{formatTimestamp(alert.timestamp)}</span>
                      {alert.actionRequired && (
                        <>
                          <span>•</span>
                          <span className="text-destructive font-medium">Action Required</span>
                        </>
                      )}
                    </div>
                  </div>
                  {!alert.read && (
                    <button
                      onClick={() => markAsRead(alert.id)}
                      className="px-3 py-1 text-sm bg-primary/10 text-primary rounded-lg hover:bg-primary/20 transition-colors"
                    >
                      Mark Read
                    </button>
                  )}
                </div>
                <p className="text-foreground">{alert.message}</p>
                {alert.actionRequired && (
                  <div className="mt-4 flex gap-3">
                    <button className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors text-sm font-medium">
                      Take Action
                    </button>
                    <button className="px-4 py-2 border border-input rounded-lg hover:bg-muted transition-colors text-sm font-medium text-foreground">
                      View Details
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}

        {filteredAlerts.length === 0 && (
          <div className="bg-card rounded-xl p-12 shadow-card border border-border text-center">
            <Icon name="BellSlashIcon" size={48} className="mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">No alerts found matching your filters</p>
          </div>
        )}
      </div>
    </div>
  );
}
